

import { Component, Mixins } from "vue-property-decorator";
import IbRadioButton from "@/components/IBButton/IBRadioButton.vue";
import IBNotificationContent from "@/components/Notification/IBNotificationContent.vue";
import { $getNotificationCountType, $postNotificationList } from "@/api/notification";
import {
	INotificationCountTypeData,
	INotificationList,
	INotificationListUpdate,
	INotificationTopic,
	ISendNotificationData
} from "@/api/notification/interface";
import { HTMLNotification, NotificationCountByTopicType, tabBtnData } from "@/components/Notification/interface";
import IBNotificationRead from "@/components/Notification/IBNotificationRead";
import IBSpinnerLoading from "@/components/IBLoading/IBSpinnerLoading.vue";

const startPageNumber: number = 1;

@Component({
	components: {
		IBNotificationContent,
		IbRadioButton,
		IBSpinnerLoading
	}
})
export default class NotificationPanel extends Mixins(IBNotificationRead) {

	$refs!: {
		notificationPanel: HTMLDivElement
	}

	scrollIntersectionObserver: IntersectionObserver | null = null

	readNotificationRadius: number = 0.7

	scrollEndRadius: number = 20

	loading: boolean = false

	allPagesLoaded: boolean = false

	pageNumber: number = startPageNumber

	pageSize: number = 25

	tabBtn: tabBtnData[] = [
		{
			title: this.$t('reports.turnovers.all') as string,
			number: 0,
			type: INotificationTopic.NOTIFICATIONS_TOPICS
		},
		{
			title: this.$t('notification.operation') as string,
			number: 0,
			type: INotificationTopic.NOTIFICATIONS_OPERATIONS
		},
		{
			title: this.$t('notification.newsAndOffers') as string,
			number: 0,
			type: INotificationTopic.NOTIFICATIONS_NEWS
		}
	];

	notList: tabBtnData[] = [
		{
			title: this.$t("home.notification.emptyText") as string,
			date: +new Date()
		}
	];

	paymentList: Array<INotificationListUpdate | tabBtnData> = [];

	notificationBtnSwitch: INotificationTopic = INotificationTopic.NOTIFICATIONS_TOPICS;

	notificationContentHeight: number = 0;

	mounted() {
		this.getNotificationList();

		this.getNotificationCount();

		this.setScrollIntersectionObserver();
	}

	async getNotificationList(resetList: boolean = true): Promise<Array<INotificationListUpdate | tabBtnData>> {
		if (resetList) this.resetPage();
		if (this.loading) return this.paymentList;

		this.loading = true;

		return $postNotificationList(this.postSendNotificationData(), { disableLoader: true })
			.then((list: INotificationList[]) => {
				this.loading = false;

				if (list.length < this.pageSize) this.allPagesLoaded = true;

				if (!list.length && (resetList || !this.paymentList.length))
					return this.paymentList = this.notList;

				list = list.map((i) => this.newList(i));

				return this.paymentList = resetList
					? list
					: [ ...this.paymentList, ...list ];
			});
	}

	readNotifications(ids: number[]) {
		const paymentList = this.paymentList as INotificationListUpdate[];
		const decreaseCounts: NotificationCountByTopicType = {};

		for (let i = 0; i < paymentList.length; i++) {
			if (ids.includes(paymentList[i].id)) {
				const { topicType } = paymentList[i];

				paymentList[i].isRead = true;

				if (decreaseCounts.hasOwnProperty(topicType))
					decreaseCounts[topicType]!++;
				else
					decreaseCounts[topicType] = 1;
			}
		}

		this.decreaseCountInFilters(decreaseCounts);
	}

	decreaseCountInFilters(counts: NotificationCountByTopicType) {
		this.tabBtn.forEach(tabBtn => {
			if (counts[tabBtn.type!]) tabBtn.number! -= counts[tabBtn.type!]!;
		});
	}

	getNotificationCount() {
		$getNotificationCountType().then((r: INotificationCountTypeData[]) => {
			if (!r) return;

			this.tabBtn.forEach((i: tabBtnData, idx: number) => i.number = r[idx].count);

			for (let i = 0; i < this.tabBtn.length; i++) {
				if (this.tabBtn[i].type === INotificationTopic.NOTIFICATIONS_TOPICS) {
					if (!this.tabBtn[i].number) {
						this.tabBtn[i].number = this.tabBtn
							.filter(tab => tab.type !== INotificationTopic.NOTIFICATIONS_TOPICS)
							.reduce((a, b) => a += b.number || 0, 0);
					}

					break;
				}
			}
		});
	}

	onClickBtn(v: INotificationTopic) {
		this.notificationBtnSwitch = v;

		this.getNotificationList();
	}

	closeNotification() {
		this.$emit("closeNotification");
	}

	setScrollIntersectionObserver() {
		this.createIntersectionObserver(this.$refs.notificationPanel);

		return this;
	}

	scrollHandler(e: Event) {
		const el = e.target as HTMLDivElement;
		const scrollEndRadius = el.scrollHeight - el.clientHeight - el.scrollTop;

		if (scrollEndRadius <= this.scrollEndRadius) this.loadNextPage();
	}

	loadNextPage() {
		if (this.allPagesLoaded || this.loading) return;

		this.pageNumber++;

		this.getNotificationList(false)
			.then(() => this.destroyScrollIntersectionObserver().setScrollIntersectionObserver());
	}

	resetPage() {
		this.paymentList = [];
		this.allPagesLoaded = this.loading = false;
		this.pageNumber = startPageNumber;
	}

	destroyScrollIntersectionObserver(): this {
		if (this.scrollIntersectionObserver) {
			this.scrollIntersectionObserver.disconnect();

			this.scrollIntersectionObserver = null;
		}

		return this;
	}

	createIntersectionObserver(root: HTMLElement | null): IntersectionObserver {
		const callback = (entries: IntersectionObserverEntry[]) => this.addReadNotificationIds(
			entries
				.map(({ isIntersecting, target }) => isIntersecting
					&& (target as HTMLNotification).NOTIFICATION_ID)
				.filter(id => id && !this.isReadNotification(id)) as number[]
		);

		return this.scrollIntersectionObserver = new IntersectionObserver(
			callback,
			{
				root,
				rootMargin: '0px',
				threshold: this.readNotificationRadius
			}
		);
	}

	isReadNotification(id: number): boolean {
		return this.paymentList.some((i: any) => i.id === id && i.isRead);
	}

	postSendNotificationData(): ISendNotificationData {
		const topicUpdate: undefined | INotificationTopic = (
			this.notificationBtnSwitch === INotificationTopic.NOTIFICATIONS_TOPICS
				? undefined
				: this.notificationBtnSwitch
		);

		return {
			topicType: topicUpdate,
			elementsOnPage: this.pageSize,
			pageNumber: this.pageNumber
		};
	}

	newList(i: INotificationList): INotificationListUpdate {
		return {
			id: i.id,
			type: i.type,
			topicType: i.topicType,
			title: i.title,
			text: i.text,
			isRead: i.isRead,
			createdAt: i.createdAt,
			data: i.data,
			date: i.createdAt,
			imageUrl: i.imageUrl,
			htmlText: i.htmlText
		};
	}

}

